import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IPersistentStorage } from '@webapp/infrastructure/interfaces/services/IPersistentStorage'
import { type IUserRepository } from '@webapp/platform/iam/business-logic/interfaces/IUserRepository'
import { mapDetailedUserApiModel } from '@webapp/platform/iam/data-access/mapper/mapDetailedUserApiModel'
import { mapUserSettingApiModel } from '@webapp/platform/iam/data-access/mapper/mapUserSettingApiModel'
import { type EditUserDetailsPayload } from '@webapp/platform/iam/data-access/types/editUserDetailsPayload'
import { type GetUserDetailsResponse } from '@webapp/platform/iam/data-access/types/getUserDetailsResponse'
import { type GetUserSettingResponse } from '@webapp/platform/iam/data-access/types/getUserSettingResponse'
import { type UpdateProfileImagePayload } from '@webapp/platform/iam/data-access/types/updateProfileImagePayload'
import { type UpdateProfileImageResponse } from '@webapp/platform/iam/data-access/types/updateProfileImageResponse'
import { type UpdatePurchasePriceLimitPayload } from '@webapp/platform/iam/data-access/types/updatePurchasePriceLimitPayload'
import { type DetailedUserConstructor } from '@webapp/platform/iam/entities/DetailedUser'
import {
  type UserPreferenceKey,
  UserPreferenceKeys,
} from '@webapp/platform/iam/entities/enums/UserPreferenceKeys'
import { type UserConstructor } from '@webapp/platform/iam/entities/User'
import { type UserCountByTypeConstructor } from '@webapp/platform/iam/entities/UserCountByType'
import { type UserPurchasePriceLimitConstructor } from '@webapp/platform/iam/entities/UserPurchasePriceLimit'
import { mapUserApiModel } from '../../mapper/mapUserApiModel'
import { mapUserCountByTypeApiModel } from '../../mapper/mapUserCountByTypeApiModel'
import { type InviteUserPayload } from '../../types/createUserPayload'
import { type GetUserCountByTypeResponse } from '../../types/getUserCountByTypeResponse'
import { type GetUserListResponse } from '../../types/getUserListResponse'

interface Dependency {
  httpClient: IHttpClient
  persistentStorage: IPersistentStorage
}

export class UserRepository implements IUserRepository {
  constructor(private readonly deps: Dependency) {}

  private getPreferenceStorageKey(
    userId: number,
    key: UserPreferenceKey
  ): string {
    // Per-key storage key builders. ORDER_REQUEST_FLOW keeps its pre-existing
    // key so preferences saved before this moved into IAM remain intact.
    const storageKeyBuilders: Record<UserPreferenceKey, string> = {
      [UserPreferenceKeys.ORDER_REQUEST_FLOW]: `procurify.order-request-creation-flow-preference_${userId}`,
    }

    return storageKeyBuilders[key]
  }

  async getPreference(
    userId: number,
    key: UserPreferenceKey
  ): Promise<Nullable<string>> {
    return this.deps.persistentStorage.get(
      this.getPreferenceStorageKey(userId, key)
    )
  }

  setPreference(userId: number, key: UserPreferenceKey, value: string): void {
    try {
      this.deps.persistentStorage.set(
        this.getPreferenceStorageKey(userId, key),
        value
      )
    } catch (error) {
      throw throwRepoErrorFromApiV3Error(error)
    }
  }

  async list(
    isActive?: boolean,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<UserConstructor>> {
    try {
      const res = await this.deps.httpClient.get<GetUserListResponse>(
        `/api/v3/users/`,
        {
          params: {
            is_active: isActive,
            ...formatRepoFiltersToApiFilters(params),
          },
        }
      )

      const users = res.data.data
      const metadata = res.data.metadata

      return {
        data: users.map((user) => mapUserApiModel(user)),
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getDetails(
    userId: number | uuid
  ): Promise<RepoStandardResponse<DetailedUserConstructor>> {
    try {
      const userResponse =
        await this.deps.httpClient.get<GetUserDetailsResponse>(
          `/api/v3/users/${userId}/`,
          {
            params: {
              format: 'json',
            },
          }
        )
      const { data: detailedUser } = userResponse.data

      return {
        data: mapDetailedUserApiModel(detailedUser),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async editDetails(
    userId: number,
    payload: EditUserDetailsPayload
  ): Promise<void> {
    try {
      await this.deps.httpClient.put(`/api/v3/users/${userId}/`, payload)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getPurchasePriceLimit(
    userId: number
  ): Promise<RepoStandardResponse<UserPurchasePriceLimitConstructor>> {
    const userThresholdSettingsResponse =
      await this.deps.httpClient.get<GetUserSettingResponse>(
        `/api/v2/user/${userId}/setting/`
      )

    const { data: userThresholdSettings } = userThresholdSettingsResponse.data

    return {
      data: mapUserSettingApiModel(userThresholdSettings),
    }
  }

  async updatePurchasePriceLimit(
    userId: number,
    payload: UpdatePurchasePriceLimitPayload
  ): Promise<void> {
    await this.deps.httpClient.put(`/api/v2/user/${userId}/setting/`, payload)
  }

  async resetUserMFA(userId?: number): Promise<void> {
    if (!userId) {
      throw new Error('Missing userId')
    }

    await this.deps.httpClient.delete(
      `/api/v3/users/${userId.toString()}/authentication-methods`
    )
  }

  async inviteUser(data: InviteUserPayload[]): Promise<void> {
    try {
      await this.deps.httpClient.post('/api/v3/users/', data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async delete(userId: number): Promise<void> {
    try {
      await this.deps.httpClient.delete(`/api/v3/users/${userId}/`)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async activate(userId: number): Promise<void> {
    try {
      await this.deps.httpClient.put(`/api/v3/users/${userId}/reactivate/`, {})
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateProfileImage(
    userId: number,
    payload: UpdateProfileImagePayload
  ): Promise<RepoStandardResponse<UserConstructor>> {
    try {
      const userResponse =
        await this.deps.httpClient.patch<UpdateProfileImageResponse>(
          `/api/v3/users/${userId}/`,
          payload
        )

      const { data: user } = userResponse.data

      return {
        data: mapUserApiModel(user),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getCounts(): Promise<RepoStandardResponse<UserCountByTypeConstructor>> {
    try {
      const res = await this.deps.httpClient.get<GetUserCountByTypeResponse>(
        '/api/v3/users/counts'
      )

      const { data: countsData } = res.data

      return {
        data: mapUserCountByTypeApiModel(countsData),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
